// src/products/ProductCard.js
import React, { useState, useEffect } from 'react';
import { getCleanImageUrl, hasRealProductImage } from '../utils/imageHelper';
import { decodeHtml, parsePrice, formatPackingInfo } from '../utils/textHelper';

// Medicine icon SVG for when no image is available
const MedicineIcon = ({ category = '' }) => {
    const lower = category.toLowerCase();
    if (lower.includes('syrup') || lower.includes('susp') || lower.includes('liquid')) {
        return (
            <svg viewBox="0 0 64 80" className="w-16 h-20 text-emerald-500 opacity-70" fill="none">
                <rect x="18" y="20" width="28" height="48" rx="6" fill="currentColor" opacity="0.15"/>
                <rect x="22" y="10" width="20" height="14" rx="4" fill="currentColor" opacity="0.25"/>
                <rect x="18" y="44" width="28" height="24" rx="6" fill="currentColor" opacity="0.3"/>
                <line x1="24" y1="52" x2="40" y2="52" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="24" y1="57" x2="36" y2="57" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        );
    }
    if (lower.includes('cream') || lower.includes('gel') || lower.includes('oint')) {
        return (
            <svg viewBox="0 0 64 80" className="w-16 h-20 text-rose-500 opacity-70" fill="none">
                <ellipse cx="32" cy="60" rx="20" ry="10" fill="currentColor" opacity="0.2"/>
                <rect x="20" y="20" width="24" height="40" rx="12" fill="currentColor" opacity="0.2"/>
                <rect x="26" y="10" width="12" height="14" rx="4" fill="currentColor" opacity="0.3"/>
            </svg>
        );
    }
    if (lower.includes('drop')) {
        return (
            <svg viewBox="0 0 64 80" className="w-16 h-20 text-cyan-500 opacity-70" fill="none">
                <path d="M32 10 C32 10 12 40 12 54 C12 65 21 72 32 72 C43 72 52 65 52 54 C52 40 32 10 32 10Z" fill="currentColor" opacity="0.2"/>
                <path d="M32 20 C32 20 18 44 18 54 C18 62 24 68 32 68" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
            </svg>
        );
    }
    if (lower.includes('inj') || lower.includes('vial')) {
        return (
            <svg viewBox="0 0 64 80" className="w-16 h-20 text-violet-500 opacity-70" fill="none">
                <rect x="22" y="22" width="20" height="46" rx="4" fill="currentColor" opacity="0.2"/>
                <rect x="26" y="12" width="12" height="10" rx="2" fill="currentColor" opacity="0.3"/>
                <line x1="32" y1="4" x2="32" y2="12" stroke="currentColor" strokeWidth="2.5"/>
            </svg>
        );
    }
    // Default: tablet/capsule
    return (
        <svg viewBox="0 0 80 40" className="w-20 h-12 text-indigo-500 opacity-70" fill="none">
            <rect x="2" y="8" width="76" height="24" rx="12" fill="currentColor" opacity="0.15"/>
            <rect x="2" y="8" width="38" height="24" rx="12" fill="currentColor" opacity="0.25"/>
            <line x1="40" y1="8" x2="40" y2="32" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
        </svg>
    );
};

const ProductCard = ({ product, addToCart }) => {
    const productName = decodeHtml(product?.product_name || product?.Product || product?.productName || product?.name || '');
    const defaultImg = getCleanImageUrl(product, productName);
    const [displayImg, setDisplayImg] = useState(defaultImg);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
        setDisplayImg(defaultImg);
    }, [defaultImg, product]);

    if (!product || !product._id) return null;

    const mrpValue = parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price);

    const companyName = decodeHtml(product?.product_manufactured || product.Company || product.company || product.brand);
    const pPack = product?.Pack || product?.pack || product?.packSize || '';
    const pUnit = product?.Unit || product?.unit || product?.measurementUnit || '';
    const category = product?.category || product?.Category || product?.sub_category || '';
    const unitInfo = formatPackingInfo(pPack, pUnit, category, productName);

    // Determine if we have a real product image
    const hasRealImage = !imageFailed && hasRealProductImage(product);

    // Vibrant category colors
    const getCategoryColors = () => {
        const lower = (productName + category).toLowerCase();
        if (lower.includes('syrup') || lower.includes('liquid') || lower.includes('susp')) return { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', card: 'bg-emerald-50/50 border-emerald-100 hover:border-emerald-300 hover:shadow-emerald-500/10' };
        if (lower.includes('cream') || lower.includes('gel') || lower.includes('oint')) return { badge: 'bg-rose-100 text-rose-700 border-rose-200', card: 'bg-rose-50/50 border-rose-100 hover:border-rose-300 hover:shadow-rose-500/10' };
        if (lower.includes('drop')) return { badge: 'bg-cyan-100 text-cyan-700 border-cyan-200', card: 'bg-cyan-50/50 border-cyan-100 hover:border-cyan-300 hover:shadow-cyan-500/10' };
        if (lower.includes('inj') || lower.includes('vial')) return { badge: 'bg-violet-100 text-violet-700 border-violet-200', card: 'bg-violet-50/50 border-violet-100 hover:border-violet-300 hover:shadow-violet-500/10' };
        if (lower.includes('cap') || lower.includes('capsule')) return { badge: 'bg-amber-100 text-amber-700 border-amber-200', card: 'bg-amber-50/50 border-amber-100 hover:border-amber-300 hover:shadow-amber-500/10' };
        return { badge: 'bg-indigo-100 text-indigo-700 border-indigo-200', card: 'bg-indigo-50/50 border-indigo-100 hover:border-indigo-300 hover:shadow-indigo-500/10' }; // default
    };
    
    const theme = getCategoryColors();

    return (
        <div className={`${theme.card} rounded-3xl border transition-all duration-400 ease-out flex flex-col h-full overflow-hidden group relative bg-white`}>
            
            {/* Subtle Gradient Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

            {/* Product Visual Area */}
            <div
                className={`relative h-44 sm:h-52 w-full flex justify-center items-center cursor-pointer overflow-hidden border-b border-gray-100 transition-colors ${
                    hasRealImage ? 'bg-white p-3 sm:p-5' : 'bg-gradient-to-b from-white to-slate-50/70 p-4'
                }`}
                onClick={() => window.open('#product/' + product._id, '_blank')}
            >
                {hasRealImage ? (
                    <img
                        src={displayImg}
                        alt={productName}
                        className="max-h-full max-w-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500 ease-out"
                        style={{
                            imageRendering: '-webkit-optimize-contrast',
                            transform: 'translateZ(0)'
                        }}
                        loading="lazy"
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center text-slate-400 select-none group-hover:scale-105 transition-transform duration-300">
                        <MedicineIcon category={category || productName} />
                        <span className="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wider">
                            {category || 'Verified Medicine'}
                        </span>
                    </div>
                )}
            </div>

            {/* Content Section */}
            <div className="p-5 sm:p-6 flex flex-col flex-grow relative">
                
                {/* Category Badge */}
                {category && (
                    <div className="mb-3">
                        <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-full border ${theme.badge}`}>
                            {category}
                        </span>
                    </div>
                )}

                {/* Brand & Name */}
                <div className="mb-4">
                    {companyName && (
                        <p className="text-[10px] sm:text-xs text-indigo-500 font-extrabold uppercase tracking-widest mb-1.5 line-clamp-1 opacity-80">{companyName}</p>
                    )}
                    <h3
                        className="font-bold text-gray-800 text-base sm:text-lg leading-snug cursor-pointer group-hover:text-indigo-600 transition-colors line-clamp-2"
                        onClick={() => window.open('#product/' + product._id, '_blank')}
                    >
                        {productName}
                    </h3>
                </div>

                {/* Unit/Pack Size */}
                {unitInfo && (
                    <div className="inline-flex items-center gap-1.5 mb-6 text-xs sm:text-sm text-gray-500 font-medium">
                        <i className="fas fa-box-open opacity-40"></i>
                        {unitInfo}
                    </div>
                )}

                {/* Price and Action (Pushed to bottom) */}
                <div className="mt-auto flex items-end justify-between pt-4">
                    <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">MRP</span>
                        <div className="flex items-baseline gap-1">
                            <span className="font-black text-xl sm:text-2xl text-gray-900 tracking-tight">
                                {mrpValue ? `₹${mrpValue.toFixed(2)}` : 'N/A'}
                            </span>
                        </div>
                    </div>
                    
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            addToCart({ ...product, imageUrl: displayImg });
                        }}
                        className="relative overflow-hidden flex items-center justify-center bg-gray-50 text-indigo-600 hover:text-white font-bold p-3 sm:px-6 sm:py-2.5 rounded-2xl transition-all duration-300 group/btn"
                        aria-label={`Add ${productName} to cart`}
                    >
                        {/* Button hover gradient background */}
                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-violet-500 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                        
                        <span className="hidden sm:inline relative z-10">Add to Cart</span>
                        <i className="fas fa-plus sm:hidden relative z-10 text-lg"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
